import { JSONSchema7 } from 'json-schema';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import { headingsSchema, headingsUISchema } from '../Header';
import Table from './Table';

export const tableSchema: JSONSchema7 = {
    title: 'Table',
    type: 'object',
    required: [],
    description: `This table supports rendering rows as icon amongst other things.
    It requires the "renderAsIcon" flag within the row transform function
    `,
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        id: {
            type: 'string',
            description: 'Tabel Id',
        },
        chartTitles: headingsSchema,
        columns: {
            type: 'array',
            title: 'Add columns',
            items: {
                type: 'object',
                properties: {
                    label: { type: 'string', title: 'Column header text' },
                    value: { type: 'string', title: 'Column data key' },
                    downloadLabel: { type: 'string', title: 'Download Column header' },
                    downloadValue: { type: 'string', title: 'Download Column data key' },
                    customDataKey: { type: 'string', title: 'Custom Data Key' },
                    isOnlyText: { type: 'boolean', title: 'Is only text' },
                    isSerial: { type: 'boolean', title: 'Is Serial Column' },
                    isIcon: { type: 'boolean', title: 'Is Icon Column' },
                    isResourceString: { type: 'boolean', title: 'Is Resource String' },
                    isLink: { type: 'boolean', title: 'Is Link Column' },
                    markerLabel: { type: 'string', title: 'Markers Label' },
                    disableVisibilityToggle: {
                        type: 'boolean',
                        title: 'Disable Visibility Toggle',
                    },
                },
                dependencies: {
                    isLink: {
                        oneOf: [
                            {
                                properties: {
                                    isLink: { enum: [true] },
                                    linkIcon: {
                                        type: 'string',
                                        title: 'Link Icon',
                                        description: 'for ex- fas fa-file-pdf',
                                    },
                                    linkUrl: {
                                        type: 'string',
                                        title: 'Link Text',
                                        description: 'enter data key for link text',
                                    },
                                },
                            },
                            {
                                properties: {
                                    isLink: { enum: [false] },
                                },
                            },
                        ],
                    },
                },
            },
        },
        rows: {
            type: 'string',
            description: 'Row array data key',
        },
        noDataMessage: {
            type: 'string',
            title: 'Text to show when no data',
        },
        newFundDataMessage: {
            type: 'string',
            title: 'Text to show when funds are new',
        },
        emptyRowsMessage: {
            type: 'string',
            title: 'Text to show when empty rows',
        },
        transformRowsData: {
            type: 'boolean',
            title: 'Transform Rows Data',
            default: false,
        },
        isCollapsible: {
            type: 'boolean',
            enum: [true, false],
            description: 'Make table collapsible or not',
            default: false,
            title: 'Collapsible',
        },
        nullValueAriaLabel: {
            type: 'string',
            title: 'Aria Label for null values',
        },
        iconIndicators: {
            type: 'array',
            title: 'Configure different types of Icon Indicators',
            items: {
                type: 'object',
                properties: {
                    activeIconName: {
                        type: 'string',
                        title: 'Active Icon Name',
                        default: '',
                    },
                    inactiveIconName: {
                        type: 'string',
                        title: 'Inactive Icon Name',
                        default: '',
                    },
                },
            },
        },
        filterData: {
            type: 'boolean',
            enum: [true, false],
            title: 'Filter table data',
            default: false,
        },
        enableColumnToggling: {
            type: 'boolean',
            title: 'Enable column toggling',
            description: '',
        },
        enableRowToggling: {
            type: 'boolean',
            title: 'Enable row toggling',
            description: '',
        },
        disableTabStaticData: {
            type: 'boolean',
            title: 'Disable tabbing through static data',
            description: '',
        },
    },
    dependencies: {
        isCollapsible: {
            oneOf: [
                {
                    properties: {
                        isCollapsible: { enum: [true] },
                        collapsibleElement: {
                            type: 'string',
                            title: 'Choose Collapsible Element',
                            enum: ['Label', 'Icon'],
                            default: 'Label',
                        },
                        collapsibleElementPosition: {
                            type: 'string',
                            title: 'Collapsible Element Position',
                            enum: ['Right', 'Left'],
                            default: 'Right',
                        },
                        defaultCollapse: {
                            type: 'string',
                            title: 'Default Collapse behaviour',
                            enum: ['Show', 'Hide'],
                            default: 'Hide',
                        },
                        excludedRow: {
                            type: 'number',
                            description: 'Row number to stay visible when table collapse',
                            default: false,
                            title: 'Exclude Row Number',
                        },
                    },
                },
                {
                    properties: {
                        isCollapsible: { enum: [false] },
                    },
                },
            ],
        },
        enableColumnToggling: {
            oneOf: [
                {
                    properties: {
                        enableColumnToggling: { enum: [true] },
                        columnTogglingTitle: {
                            type: 'string',
                            title: 'Column toggling title',
                            description: 'Enter value',
                        },
                        columnTogglingSubtitle: {
                            type: 'string',
                            title: 'Column toggling subtitle',
                            description: 'Enter value',
                        },
                        columnTogglingSaveText: {
                            type: 'string',
                            title: 'Column toggling save text',
                            description: 'Enter value',
                        },
                        columnTogglingCancelText: {
                            type: 'string',
                            title: 'Column toggling cancel text',
                            description: 'Enter value',
                        },
                        columnTogglingSelectAllText: {
                            type: 'string',
                            title: 'Column toggling select all text',
                            description: 'Enter value',
                        },
                        columnTogglingDeselectAllText: {
                            type: 'string',
                            title: 'Column toggling deselect all text',
                            description: 'Enter value',
                        },
                    },
                },
                {
                    properties: {
                        enableColumnToggling: { enum: [false] },
                    },
                },
            ],
        },
        enableRowToggling: {
            oneOf: [
                {
                    properties: {
                        enableRowToggling: { enum: [true] },
                        rowTogglingTitle: {
                            type: 'string',
                            title: 'Row toggling title',
                            description: 'Enter value',
                        },
                        rowTogglingSubtitle: {
                            type: 'string',
                            title: 'Row toggling subtitle',
                            description: 'Enter value',
                        },
                        rowTogglingSaveText: {
                            type: 'string',
                            title: 'Row toggling save text',
                            description: 'Enter value',
                        },
                        rowTogglingCancelText: {
                            type: 'string',
                            title: 'Row toggling cancel text',
                            description: 'Enter value',
                        },
                        rowTogglingSelectAllText: {
                            type: 'string',
                            title: 'Row toggling select all text',
                            description: 'Enter value',
                        },
                        rowTogglingDeselectAllText: {
                            type: 'string',
                            title: 'Row toggling deselect all text',
                            description: 'Enter value',
                        },
                        rowTogglingLabelDataKey: {
                            type: 'string',
                            title: 'Row toggling label data key',
                            description: 'Enter value',
                        },
                    },
                },
                {
                    properties: {
                        enableRowToggling: { enum: [false] },
                    },
                },
            ],
        },
        collapsibleElement: {
            oneOf: [
                {
                    properties: {
                        collapsibleElement: { enum: ['Label'] },
                        collapsibleShowLabel: {
                            type: 'string',
                            title: 'Show Rows Label',
                            description: 'Label to show when content not visible',
                        },
                        collapsibleHideLabel: {
                            type: 'string',
                            title: 'Hide Rows Label',
                            description: 'Lable to show when content visible',
                        },
                    },
                    required: ['collapsibleShowLabel', 'collapsibleHideLabel'],
                },
                {
                    properties: {
                        collapsibleElement: { enum: ['Icon'] },
                        iconShowContent: {
                            type: 'string',
                            title: 'Show Rows Icon Name',
                            description: 'Icon to show when content not visible',
                            default: 'fas fa-plus-circle',
                        },
                        iconHideContent: {
                            type: 'string',
                            title: 'Hide Rows Icon Name',
                            description: 'Icon to show when content visible',
                            default: 'fas fa-minus-circle',
                        },
                    },
                    required: ['iconShowContent', 'iconHideContent'],
                },
            ],
        },
        transformRowsData: {
            oneOf: [
                {
                    properties: {
                        transformRowsData: { enum: [true] },
                        functionToTransformRows: {
                            type: 'string',
                            description: 'Function to transform Rows',
                            default: 'return (data) => { return data}',
                        },
                    },
                },
                {
                    properties: {
                        transformRowsData: { enum: [false] },
                    },
                },
            ],
        },
        filterData: {
            oneOf: [
                {
                    properties: {
                        filterData: { enum: [true] },
                        filterBy: {
                            type: 'string',
                            title: 'Enter data property',
                            description: 'Enter data property to filter the data by',
                            default: '',
                        },
                        filterWithUrlParam: {
                            type: 'boolean',
                            title: 'Get filter list from url',
                            enum: [true, false],
                            default: 'true',
                        },
                    },
                    dependencies: {
                        filterWithUrlParam: {
                            oneOf: [
                                {
                                    properties: {
                                        filterWithUrlParam: { enum: [true] },
                                        filterUrlParam: {
                                            type: 'string',
                                            title: 'Enter url param',
                                            default: '',
                                        },
                                    },
                                    dependencies: {},
                                },
                                {
                                    properties: {
                                        filterWithUrlParam: { enum: [false] },
                                        filterWith: {
                                            type: 'string',
                                            title: 'Enter data property folr filter list',
                                            default: '',
                                        },
                                    },
                                },
                            ],
                        },
                    },
                },
                {
                    properties: {
                        filterData: { enum: [false] },
                    },
                },
            ],
        },
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
    },
};

export const tableSchemaGroups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'id',
            'chartTitles',
            'columns',
            'rows',
            'noDataMessage',
            'newFundDataMessage',
            'emptyRowsMessage',
            'isCollapsible',
            'collapsibleElement',
            'collapsibleShowLabel',
            'collapsibleHideLabel',
            'iconShowContent',
            'iconHideContent',
            'collapsibleElementPosition',
            'defaultCollapse',
            'excludedRow',
            'nullValueAriaLabel',
            'iconIndicators',
            'filterData',
            'filterBy',
            'filterWithUrlParam',
            'filterUrlParam',
            'filterWith',
            'enableColumnToggling',
            'columnTogglingTitle',
            'columnTogglingSubtitle',
            'columnTogglingSaveText',
            'columnTogglingCancelText',
            'columnTogglingSelectAllText',
            'columnTogglingDeselectAllText',
            'enableRowToggling',
            'rowTogglingTitle',
            'rowTogglingSubtitle',
            'rowTogglingSaveText',
            'rowTogglingCancelText',
            'rowTogglingSelectAllText',
            'rowTogglingDeselectAllText',
            'rowTogglingLabelDataKey',
        ],
        'Transform Data': ['transformRowsData', 'functionToTransformRows'],
        Accessibility: ['disableTabStaticData'],
    },
];

const TableWidget: commonTypes.IWidget = {
    schema: tableSchema,
    uiSchema: {
        'ui:groups': tableSchemaGroups,
        ...customSchemas.textSchema.TextSchema.uiSchema,
        ...customSchemas.styleSchema.StyleSchema.uiSchema,
        ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
        styles: {
            'ui:widget': 'CSSEditor',
        },
        chartTitles: {
            ...headingsUISchema,
        },
        columns: {
            items: {
                'ui:field': 'CollapsibleFieldTemplate',
                label: { 'ui:widget': 'ResourcePicker' },
                value: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{}}' },
                downloadLabel: { 'ui:widget': 'ResourcePicker' },
                downloadValue: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{}}' },
                customDataKey: { 'ui:widget': 'CustomTextInput' },
                isOnlyText: { 'ui:widget': 'CustomCheckbox' },
                isSerial: { 'ui:widget': 'CustomCheckbox' },
                isIcon: { 'ui:widget': 'CustomCheckbox' },
                isResourceString: { 'ui:widget': 'CustomCheckbox' },
                markerLabel: { 'ui:widget': 'CustomTextInput' },
                isLink: { 'ui:widget': 'CustomCheckbox' },
                linkIcon: { 'ui:widget': 'CustomTextInput' },
                linkUrl: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{}}' },
                disableVisibilityToggle: { 'ui:widget': 'CustomCheckbox' },
            },
        },
        id: {
            'ui:widget': 'ResourcePicker',
        },
        rows: {
            'ui:widget': 'ResourcePicker',
        },
        nullValueAriaLabel: {
            'ui:widget': 'ResourcePicker',
        },
        noDataMessage: {
            'ui:widget': 'ResourcePicker',
        },
        newFundDataMessage: {
            'ui:widget': 'ResourcePicker',
        },
        emptyRowsMessage: {
            'ui:widget': 'ResourcePicker',
        },
        isCollapsible: {
            'ui:widget': 'CustomCheckbox',
        },
        collapsibleElement: { 'ui:widget': 'radio' },
        collapsibleElementPosition: { 'ui:widget': 'radio' },
        defaultCollapse: { 'ui:widget': 'radio' },
        functionToTransformRows: { 'ui:widget': 'JSEditor' },
        filterData: { 'ui:widget': 'CustomCheckbox' },
        filterBy: { 'ui:widget': 'CustomTextInput' },
        filterWithUrlParam: { 'ui:widget': 'CustomCheckbox' },
        filterUrlParam: { 'ui:widget': 'CustomTextInput' },
        filterWith: { 'ui:widget': 'CustomTextInput' },
        enableColumnToggling: { 'ui:widget': 'CustomCheckbox' },
        columnTogglingTitle: { 'ui:widget': 'ResourcePicker' },
        columnTogglingSubtitle: { 'ui:widget': 'ResourcePicker' },
        columnTogglingSaveText: { 'ui:widget': 'ResourcePicker' },
        columnTogglingCancelText: { 'ui:widget': 'ResourcePicker' },
        columnTogglingSelectAllText: { 'ui:widget': 'ResourcePicker' },
        columnTogglingDeselectAllText: { 'ui:widget': 'ResourcePicker' },
        disableTabStaticData: { 'ui:widget': 'CustomCheckbox' },
        enableRowToggling: { 'ui:widget': 'CustomCheckbox' },
        rowTogglingTitle: { 'ui:widget': 'ResourcePicker' },
        rowTogglingSubtitle: { 'ui:widget': 'ResourcePicker' },
        rowTogglingSaveText: { 'ui:widget': 'ResourcePicker' },
        rowTogglingCancelText: { 'ui:widget': 'ResourcePicker' },
        rowTogglingSelectAllText: { 'ui:widget': 'ResourcePicker' },
        rowTogglingDeselectAllText: { 'ui:widget': 'ResourcePicker' },
        rowTogglingLabelDataKey: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{}}' },
    },
    templateView: Table,
    id: 'Table',
    group: 'table',
    contentPlaceHolders: ['children'],
    initialData: (_context?: Record<string, unknown>) => ({
        columns: [{ label: 'header1' }, { label: 'header2' }, { label: 'header3' }],
    }),
};

export default TableWidget;
